import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { IMapMarker, MapType } from '@boma/client-shared';
import { ILocationAddress } from '@boma/supplier-portal/shared/entities';
import {
  HelperFunctionsService,
  LoggingService,
} from '@boma/supplier-portal/shared/services';

import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { GoogleMap, MapGeocoder } from '@angular/google-maps';
import { MapsService } from '@boma/website/shared';
import { Marker } from '@googlemaps/markerclusterer/dist/marker-utils';
import { Observable, share, tap } from 'rxjs';

@Component({
  selector: 'boma-location-picker-map',
  templateUrl: './location-picker-map.component.html',
  styleUrls: ['./location-picker-map.component.scss'],
})
export class LocationPickerMapComponent implements OnChanges {
  @ViewChild(GoogleMap) googleMap: GoogleMap;
  // Assuming you have access to the map object
  currentZoomLevel;
  maxZoomLevel = 20;

  // Determine if it is the last cluster by comparing the current zoom level with the max zoom level
  fullZoom;

  latitude = 14.604847;
  longitude = 16.422421;
  @Input() address: ILocationAddress;
  @Input() title = 'Map Marker and Address';
  @Output() handleSelectedMarkers = new EventEmitter<Marker[]>();
  mapOptions: google.maps.MapOptions = {
    mapTypeControlOptions: {},
    center: { lat: 0, lng: 0 },
  };
  logger = inject(LoggingService);
  markerClusterer?: MarkerClusterer;
  // Options to pass along to the marker clusterer
  //https://cloud.google.com/blog/products/maps-platform/how-cluster-map-markers

  clusterOptions = {
    imagePath: '/assets/layout/images/interface/maps/m',
    gridSize: 30,
    zoomOnClick: true,
    maxZoom: 20,

    styles: [
      {
        textColor: '#ffffff',
        textSize: 14,
        width: 60,
        height: 60,
      },
      {
        textColor: '#000000',
        textSize: 16,
        width: 80,
        height: 80,
      },
      // Add more styles for different cluster sizes as needed
    ],
  };

  newAddress: ILocationAddress = {
    line1: '',
    line2: '',
    line3: '',
    line4: '',
    town: '',
    province: '',
    postalCode: '',
    id: '',
  };
  typedAddress = '';

  baseMap: google.maps.Map;

  @Input() justMap = false;
  @Input() zoom = 8;
  @Input() height = '400px';
  @Input() mapCenter: IMapMarker = null;
  @Input() location: [number, number] = null; //[long, lat] // Todo convert to imapmarker
  @Input() markers: IMapMarker[] = [];
  @Input() enableSelection = true;
  @Input() enableSearch = true;
  @Input() retrieveAddress = true;
  @Input() mapTypeId: MapType = MapType['hybrid'];
  @Input() mapTypeControls = true;
  @Input() editMode = false;
  @Output() setPoint = new EventEmitter();
  @ViewChild('search')
  public searchElementRef: ElementRef;
  formattedAddress: string[];
  markerPlain = {
    url: 'assets/layout/images/interface/maps/pin-plain.svg',
    scaledSize: { height: 60, width: 60 },
  };

  markerMove = {
    url: 'assets/layout/images/interface/maps/pin-move.svg',
    scaledSize: { height: 60, width: 60 },
  };

  userSelectedCoordinates = {
    long: null,
    lat: null,
  };

  popupContent = `
  <div>
    <h3>Cluster Popup Title</h3>
    <p>Popup content goes here</p>
  </div>
`;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mapTypeId) {
      this.updateMapOptions({ mapTypeId: changes.mapTypeId.currentValue });
    }
    if (changes.mapCenter) {
      const marker = changes.mapCenter.currentValue as IMapMarker;
      this.updateMapOptions({
        center: new google.maps.LatLng({ lng: marker.long, lat: marker.lat }),
      });
    }
  }

  private updateMapOptions(options: Partial<google.maps.MapOptions>) {
    this.logger.logDebug('UPDATING MAP OPTIONS', options);
    this.mapOptions = { ...this.mapOptions, ...options };
  }

  loadedMapsApi$: Observable<boolean>;

  constructor(
    private ngZone: NgZone,
    private helperService: HelperFunctionsService,
    private geocoder: MapGeocoder,
    private mapsService: MapsService
  ) {}

  ngOnInit(): void {
    this.loadedMapsApi$ = this.mapsService.loadedGoogleMapsApi$.pipe(
      tap((apiLoaded) => {
        if (apiLoaded) {
          if (this.location == null) {
            this.setCurrentPosition();
          } else {
            this.latitude = this.location[1];
            this.longitude = this.location[0];
          }
        }
      }),
      share()
    );
  }

  private setCurrentPosition() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.emitPoint();
      });
    }
  }

  mapClicked($event: any) {
    if (!this.enableSelection) {
      return;
    }
    console.log($event);

    this.latitude = $event.coords.lat;
    this.longitude = $event.coords.lng;
    this.getAddress(this.latitude, this.longitude);
  }

  markerDragEnd($event: any) {
    this.logger.logDebug('MARKER DRAG END', $event);
    this.typedAddress = '';

    if (!this.enableSelection && !this.editMode) {
      return;
    }
    this.latitude = $event.coords.lat;
    this.longitude = $event.coords.lng;
    this.getAddress(this.latitude, this.longitude);
  }

  getAddress(latitude, longitude) {
    this.geocoder
      .geocode({
        location: { lat: Number(latitude), lng: Number(longitude) },
      })
      .subscribe(({ results, status }) => {
        if (status === 'OK') {
          if (results[0]) {
            this.newAddress = {
              line1: '',
              line2: '',
              line3: '',
              line4: '',
              town: '',
              province: '',
              postalCode: '',
              id: '',
            };

            this.newAddress['line1'] = '';
            const streetAddress = {
              streetNumber: '',
              streetName: '',
            };
            this.newAddress['line2'] = '';
            this.newAddress['line3'] = results[0].formatted_address;
            if (results[0]?.plus_code?.global_code) {
              this.newAddress['line3'] =
                this.newAddress['line3'] +
                '; Global Code: ' +
                results[0]?.plus_code.global_code;
            }
            this.formattedAddress = results[0].formatted_address.split(',');
            const components = results[0].address_components;
            Object.keys(components).map((key, index) => {
              if (components[key].types?.indexOf('street_number') > -1) {
                streetAddress.streetNumber = components[key].long_name; // number
              }

              if (components[key].types?.indexOf('route') > -1) {
                streetAddress.streetName = components[key].long_name; // street
              }

              if (components[key].types?.indexOf('sublocality_level_1') > -1) {
                this.newAddress['line4'] = components[key].long_name; // suburb
              }
              if (components[key].types?.indexOf('locality') > -1) {
                this.newAddress['town'] = components[key].long_name;
              }
              if (
                components[key].types?.indexOf('administrative_area_level_1') >
                -1
              ) {
                this.newAddress['province'] = components[key].long_name;
              }
              if (components[key].types?.indexOf('postal_code') > -1) {
                this.newAddress['postalCode'] = components[key].long_name;
              }
            });

            if (streetAddress.streetName && streetAddress.streetNumber) {
              this.newAddress['line1'] =
                streetAddress.streetNumber + ' ' + streetAddress.streetName;
            } else {
              // we need clarification on the address it's potentially not available on googlemaps
            }

            this.newAddress = this.helperService.sanitizeParams(
              this.newAddress
            );

            this.emitPoint();
          } else {
            window.alert('No results found');
          }
        } else {
          window.alert('Geocoder failed due to: ' + status);
        }
      });
  }

  onMapReady(map: google.maps.Map) {
    // TODO THERE ARE TWO OF THESE COMPONENTS MERGE THEM!!!
    if (this.location == null && this.mapCenter == null) {
      this.getUsersDeviceCurrentPosition(); // get the users position from device, we have no geo point
    } else if (this.location) {
      // TODO convert location into object IMapMarker
      this.latitude = this.location[0];
      this.longitude = this.location[1];
    } else {
      this.longitude = this.mapCenter.long;
      this.latitude = this.mapCenter.lat;
    }
    map.setCenter(new google.maps.LatLng(this.latitude, this.longitude));
    this.userSelectedCoordinates = {
      long: this.longitude,
      lat: this.latitude,
    };
    this.location = [this.latitude, this.longitude];

    this.baseMap = map;

    const convertedMarkers = this._convertMarkers(this.markers, map);
    this._fitBoundsToMarkers(this.markers, map);

    this.markerClusterer = new MarkerClusterer({
      map: this.baseMap,
      markers: convertedMarkers,
    });
    this._assignMarkersToClusterer(this.markerClusterer, this.markers, map);

    // Change styles after cluster is created
    // const styles = markerCluster.getStyles();
    // for (let i = 0; i < styles.length; i++) {
    //   styles[i].textColor = 'white';
    //   styles[i].textSize = 18;
    //   styles[i].height = 56;
    //   styles[i].width = 56;
    // }

    // Assuming you have created a MarkerClusterer instance called 'markerClusterer'
    this.markerClusterer.onClusterClick = (event, cluster, _) => {
      // Assuming you have access to the map object
      this.currentZoomLevel = map.getZoom();

      // Determine if it is the last cluster by comparing the current zoom level with the max zoom level
      this.fullZoom = this.currentZoomLevel >= 20; // wont read this/maxZoomLevel ... some silly reason it wont read this.clusterOptions?.maxZoom
      if (this.fullZoom) {
        const clusterMarkers = cluster.markers;

        // TODO Maybe fan out the sales when you click on this, though a list is probably better make list in the side bar with all the events
        if (clusterMarkers.length > 0) {
          //TODO LET THEM CLICK THIS TO SEE THE ORDERS?
          const infoWindowContent = `${clusterMarkers.length} Order(s) details loaded to the sidebar. Click them for more information.`;

          const infoWindow = new google.maps.InfoWindow({
            content: infoWindowContent,
          });

          // Get the cluster center position
          const clusterCenter = cluster.bounds.getCenter();

          // Open the info window at the cluster center
          infoWindow.setPosition(clusterCenter);
          infoWindow.open(map);

          this.updateMarkers(clusterMarkers);
        }
      }
    };

    if (this.justMap) return;
    // Check if we need this when not interacting with the map
    // this.mapsAPILoader.load().then(() => {
    // @ts-ignore
    this.geoCoder = new google.maps.Geocoder();

    // @ts-ignore
    const autocomplete = new google.maps.places.Autocomplete(
      this.searchElementRef.nativeElement
    );
    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        //get the place result
        // @ts-ignore
        const place: google.maps.places.PlaceResult = autocomplete.getPlace();

        //verify result
        if (place.geometry === undefined || place.geometry === null) {
          return;
        }

        //set latitude, longitude and zoom
        this.latitude = place.geometry.location.lat();
        this.longitude = place.geometry.location.lng();
        this.getAddress(this.latitude, this.longitude);
      });
    });

    if (this.address) {
      this.newAddress = { ...this.address };
      this.formattedAddress = this.newAddress['line3'].split(',');
    } else {
      this.getAddress(this.latitude, this.longitude);
    }
    // });
  }

  findCoordinates(
    lat = this.userSelectedCoordinates.lat,
    long = this.userSelectedCoordinates.long,
    zoom = 10
  ) {
    this.zoom = zoom;
    this.location = [lat, long];

    this.longitude = long;
    this.latitude = lat;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(this.latitude, this.longitude));
    this.baseMap.fitBounds(bounds);
    this.getAddress(this.latitude, this.longitude);
  }

  emitPoint() {
    this.updateMapOptions({
      center: new google.maps.LatLng({
        lng: this.longitude,
        lat: this.latitude,
      }),
    });

    this.setPoint.emit({
      point: [this.longitude, this.latitude],
      address: this.newAddress,
    });
  }

  getUsersDeviceCurrentPosition() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;

        this.zoom = 12;
        this.location = [this.latitude, this.longitude];

        this.emitPoint();
        if (!this.address) {
          this.getAddress(this.latitude, this.longitude);
        }
      });
    }
  }

  updateMarkers(clusterMarkers: Marker[]) {
    this.handleSelectedMarkers.emit(clusterMarkers);
  }

  private _assignMarkersToClusterer(
    clusterer: MarkerClusterer,
    markers: IMapMarker[],
    map: google.maps.Map
  ) {
    clusterer.clearMarkers();
    clusterer.addMarkers(this._convertMarkers(markers, map));
  }

  private _convertMarkers(
    markers: IMapMarker[],
    map: google.maps.Map
  ): google.maps.Marker[] {
    return markers.map((marker: IMapMarker) => {
      const googleMarker = new google.maps.Marker({
        position: { lat: marker.lat, lng: marker.long },
        map: map,
        // label: marker.label, // put into content box
        // externalId: marker?.externalId || null,
      });

      // Add batch ID to the marker
      // googleMarker.batchId = marker.batchId;

      const infoWindowContent = `
        <div>
          <!--<img src="path/to/image.jpg" alt="Image" style="width: 100%;">-->
          <p> ${marker.label}</p>
          <!--<a href="https://example.com">Visit Website</a>-->
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({
        content: infoWindowContent,
      });

      googleMarker.addListener('click', () => {
        map.setCenter(googleMarker.getPosition());
        infoWindow.open(map, googleMarker);
      });

      // Set the marker icon if provided in the marker object
      if (marker.icon) {
        const {
          url,
          scaledSize: { height, width },
        } = marker.icon;
        googleMarker.setIcon({
          url,
          scaledSize: new google.maps.Size(width, height),
        });
      }

      return googleMarker;
    });
  }

  private _fitBoundsToMarkers(markers: IMapMarker[], map: google.maps.Map) {
    const bounds = new google.maps.LatLngBounds();
    for (const marker of markers) {
      bounds.extend(new google.maps.LatLng(marker.lat, marker.long));
    }
    map.fitBounds(bounds);
  }
}
